import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { LocationService, FloorplanLocation } from '../../services/location.service';
import { I18nService } from '../../../../services/i18n.service';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { GuidService } from '../../../../services/guid.service';
import { AuthService } from '../../../../services/auth.service';

@Component({
  selector: 'app-location-selector',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  template: `
    <div class="location-selector">
      <!-- 地點選擇按鈕 -->
      <div class="location-button-container">
        <button class="location-select-btn" (click)="openLocationDialog()">
          <i class="fas fa-map-marker-alt"></i>
          <span>{{ selectedLocationName || ('floorplan.location.select_placeholder' | translate) }}</span>
          <i class="fas fa-chevron-down"></i>
        </button>
        <button *ngIf="selectedLocationId" class="clear-location-btn" (click)="clearSelection()" [title]="'common.clear' | translate">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- 地點選擇對話框 -->
      <div class="dialog-overlay" *ngIf="showLocationDialog" (click)="closeLocationDialog()">
        <div class="location-dialog" (click)="$event.stopPropagation()">
          <div class="dialog-header">
            <h3>{{ 'floorplan.location.select_location' | translate }}</h3>
            <button class="dialog-close" (click)="closeLocationDialog()">
              <i class="fas fa-times"></i>
            </button>
          </div>
          
          <div class="dialog-content">
            <!-- 新增地點按鈕 -->
            <button class="add-location-btn" (click)="openAddLocationDialog()">
              <i class="fas fa-plus"></i>
              {{ 'floorplan.location.add_new' | translate }}
            </button>
            
            <div class="separator">{{ 'common.or' | translate }}</div>

            <!-- 現有地點選擇 -->
            <div class="existing-locations-section">
              <label class="section-label">{{ 'floorplan.location.select_existing' | translate }}</label>
              
              <!-- 載入中 -->
              <div class="loading-item" *ngIf="loading">
                <i class="fas fa-spinner fa-spin"></i>
                {{ 'common.loading' | translate }}
              </div>
              
              <!-- 地點下拉選單 -->
              <div class="location-dropdown-wrapper" *ngIf="!loading">
                <select class="location-select" 
                        [value]="selectedLocationId || ''"
                        (change)="onLocationSelect($event)">
                  <option value="">{{ 'floorplan.location.choose_location' | translate }}</option>
                  <option *ngFor="let location of locations" 
                          [value]="location.id">
                    {{ location.location_name }}
                    <span *ngIf="location.description"> - {{ location.description }}</span>
                  </option>
                </select>
                <i class="fas fa-chevron-down dropdown-icon"></i>
              </div>
              
              <!-- 選中地點的詳細資訊 -->
              <div class="selected-location-preview" *ngIf="selectedLocation">
                <div class="location-preview-card">
                  <div class="location-name">{{ selectedLocation.location_name }}</div>
                  <div class="location-description" *ngIf="selectedLocation.description">
                    {{ selectedLocation.description }}
                  </div>
                </div>
              </div>
              
              <!-- 無資料提示 -->
              <div class="no-data" *ngIf="!loading && locations.length === 0">
                {{ 'floorplan.location.no_locations_available' | translate }}
              </div>
            </div>
            
            <!-- 對話框按鈕 -->
            <div class="dialog-actions">
              <button class="btn btn-outline" (click)="closeLocationDialog()">
                {{ 'common.cancel' | translate }}
              </button>
              <button class="btn btn-primary" 
                      (click)="confirmSelection()"
                      [disabled]="!selectedLocationId">
                {{ 'common.confirm' | translate }}
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- 新增地點對話框 -->
    <div class="dialog-overlay" *ngIf="showAddDialog" (click)="closeAddDialog()">
      <div class="add-location-dialog" (click)="$event.stopPropagation()">
        <div class="dialog-header">
          <h3>{{ 'floorplan.location.add_new_location' | translate }}</h3>
          <button class="dialog-close" (click)="closeAddDialog()">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="dialog-content">
          <div class="form-group">
            <label>{{ 'floorplan.location.location_name' | translate }} *</label>
            <input
              type="text"
              class="form-control"
              [(ngModel)]="newLocation.location_name"
              [placeholder]="'floorplan.location.location_name_placeholder' | translate"
              maxlength="100"
              #locationNameInput>
          </div>

          <div class="form-group">
            <label>{{ 'floorplan.location.description' | translate }}</label>
            <textarea
              class="form-control"
              [(ngModel)]="newLocation.description"
              [placeholder]="'floorplan.location.description_placeholder' | translate"
              rows="3"
              maxlength="500"></textarea>
          </div>
        </div>
        
        <div class="dialog-actions">
          <button class="btn btn-outline" (click)="closeAddDialog()">
            {{ 'common.cancel' | translate }}
          </button>
          <button class="btn btn-primary" 
                  (click)="createLocation()"
                  [disabled]="!newLocation.location_name?.trim() || creating">
            <i class="fas fa-spinner fa-spin" *ngIf="creating"></i>
            <i class="fas fa-plus" *ngIf="!creating"></i>
            {{ creating ? ('common.creating' | translate) : ('floorplan.location.create' | translate) }}
          </button>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./location-selector.component.scss']
})
export class LocationSelectorComponent implements OnInit, OnDestroy {
  @Input() selectedLocationId?: string;
  @Output() locationChange = new EventEmitter<string | undefined>();

  private destroy$ = new Subject<void>();

  // 地點資料
  locations: FloorplanLocation[] = [];
  selectedLocation?: FloorplanLocation;
  selectedLocationName = '';

  // UI 狀態
  showLocationDialog = false;
  loading = false;

  // 新增地點對話框
  showAddDialog = false;
  creating = false;
  newLocation: Partial<FloorplanLocation> = {};

  constructor(
    private locationService: LocationService,
    private i18nService: I18nService,
    private guidService: GuidService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.loadLocations();
    this.updateSelectedLocation();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * 載入地點列表
   */
  loadLocations() {
    this.loading = true;
    this.locationService.getLocationList(true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.locations = response.data;
            this.updateSelectedLocation();
          }
          this.loading = false;
        },
        error: (error) => {
          console.error('載入地點列表失敗:', error);
          this.loading = false;
        }
      });
  }

  /**
   * 更新選中的地點
   */
  updateSelectedLocation() {
    if (this.selectedLocationId) {
      this.selectedLocation = this.locations.find(loc => loc.id === this.selectedLocationId);
      this.selectedLocationName = this.selectedLocation?.location_name || '';
    } else {
      this.selectedLocation = undefined;
      this.selectedLocationName = '';
    }
  }

  /**
   * 開啟地點選擇對話框
   */
  openLocationDialog() {
    this.showLocationDialog = true;
  }

  /**
   * 關閉地點選擇對話框
   */
  closeLocationDialog() {
    this.showLocationDialog = false;
  }



  /**
   * 選擇地點
   */
  selectLocation(location: FloorplanLocation) {
    this.selectedLocationId = location.id;
    this.selectedLocation = location;
    this.selectedLocationName = location.location_name;
    this.showLocationDialog = false;
    this.locationChange.emit(location.id);
  }

  /**
   * 處理下拉選單的選擇
   */
  onLocationSelect(event: Event) {
    const target = event.target as HTMLSelectElement;
    const locationId = target.value;
    
    if (locationId) {
      const location = this.locations.find(loc => loc.id === locationId);
      if (location) {
        this.selectedLocationId = locationId;
        this.selectedLocation = location;
        this.selectedLocationName = location.location_name;
      }
    } else {
      this.selectedLocationId = undefined;
      this.selectedLocation = undefined;
      this.selectedLocationName = '';
    }
  }

  /**
   * 確認選擇
   */
  confirmSelection() {
    this.showLocationDialog = false;
    this.locationChange.emit(this.selectedLocationId);
  }

  /**
   * 清除選擇
   */
  clearSelection() {
    this.selectedLocationId = undefined;
    this.selectedLocation = undefined;
    this.selectedLocationName = '';
    this.locationChange.emit(undefined);
  }

  /**
   * 開啟新增地點對話框
   */
  openAddLocationDialog() {
    const userInfo = this.authService.getUserInfo();
    this.newLocation = {
      location_name: '',
      is_active: true,
      created_by: userInfo.userid || userInfo.username || 'unknown-user'
    };
    this.showAddDialog = true;
  }

  /**
   * 關閉新增地點對話框
   */
  closeAddDialog() {
    this.showAddDialog = false;
    this.creating = false;
    this.newLocation = {};
  }

  /**
   * 建立新地點
   */
  createLocation() {
    if (!this.newLocation.location_name?.trim()) {
      return;
    }

    this.creating = true;
    this.locationService.createLocation(this.newLocation as any)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            // 重新載入地點列表
            this.loadLocations();
            // 自動選擇新建立的地點
            this.selectLocation(response.data);
            this.closeAddDialog();
          }
          this.creating = false;
        },
        error: (error) => {
          console.error('建立地點失敗:', error);
          this.creating = false;
        }
      });
  }
}